import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import MovementTimeline from '@/components/MovementTimeline';
import type { MovementState } from '@/components/MovementTimeline';

interface Member {
  id: string;
  user_id: string;
  full_name: string;
  position: string;
  state: string;
  joined_at: string;
}

interface Box {
  id: string;
  name: string;
  status: string;
  current_slots: number;
  max_slots: number;
  generation: number;
  members?: Member[];
  created_at: string;
}

const positionBadge = (p: string) => {
  const map: Record<string, string> = {
    RECEIVER: 'badge-blue', BRIDGE: 'badge-yellow', NEW_DONOR: 'badge-green',
  };
  return map[p] ?? 'badge-gray';
};

const statusBadge = (s: string) => {
  const map: Record<string, string> = {
    FILLING: 'badge-yellow', ACTIVE: 'badge-green', PAYOUT: 'badge-blue',
    CLOSED: 'badge-gray', PAUSED: 'badge-red',
  };
  return map[s] ?? 'badge-gray';
};

const positionSlot = (p: string) => {
  const map: Record<string, string> = {
    RECEIVER: 'slot-receiver', BRIDGE: 'slot-bridge', NEW_DONOR: 'slot-donor',
  };
  return map[p] ?? 'slot-empty';
};

export default function BoxDetailPage() {
  const { id } = useParams<{ id: string }>();
  const currentUser = useAuthStore((s) => s.user);

  const { data, isLoading } = useQuery({
    queryKey: ['box', id],
    queryFn: async () => {
      const { data } = await api.get(`/movement/boxes/${id}`);
      return data.box as Box;
    },
    enabled: !!id,
  });

  const box = data;
  const members = box?.members ?? [];
  const emptySlots = box ? box.max_slots - members.length : 0;

  return (
    <>
      <Helmet>
        <title>{box?.name ?? `City-Box #${id?.slice(0, 6)}`} — React-City</title>
      </Helmet>

      <div className="space-y-6 animate-in">
        {isLoading ? (
          <div className="text-center py-12 text-muted">Caricamento...</div>
        ) : !box ? (
          <div className="card text-center py-12">
            <span className="text-4xl block mb-4">🔍</span>
            <h3 className="text-lg font-semibold text-heading mb-2">Box non trovato</h3>
            <Link to="/boxes" className="btn-primary mt-4">Torna ai box</Link>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-heading">{box.name}</h1>
                <p className="text-muted text-sm mt-1">
                  Generazione {box.generation} — {members.length}/{box.max_slots} membri
                </p>
              </div>
              <span className={statusBadge(box.status)}>{box.status}</span>
            </div>

            {/* Progress */}
            <div className="card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-heading">Riempimento</span>
                <span className="text-sm text-muted">{members.length}/{box.max_slots}</span>
              </div>
              <div className="w-full bg-surface-200 dark:bg-surface-800 rounded-full h-3">
                <div
                  className="bg-primary-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${(members.length / box.max_slots) * 100}%` }}
                />
              </div>
            </div>

            {/* Slot visual */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4 text-heading">Composizione</h2>
              <div className="flex flex-wrap justify-center gap-3">
                {members.map((m, i) => (
                  <div key={m.id} className="flex flex-col items-center gap-1">
                    <div className={positionSlot(m.position)}>
                      {m.position === 'RECEIVER' ? 'R' : m.position === 'BRIDGE' ? 'B' : `D${i}`}
                    </div>
                    <span className="text-xs text-muted truncate max-w-[60px]">
                      {m.full_name?.split(' ')[0] ?? '?'}
                    </span>
                  </div>
                ))}
                {Array.from({ length: emptySlots }).map((_, i) => (
                  <div key={`empty-${i}`} className="flex flex-col items-center gap-1">
                    <div className="slot-empty">?</div>
                    <span className="text-xs text-muted">—</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Members table */}
            <div className="card overflow-x-auto">
              <h2 className="text-lg font-semibold mb-4 text-heading">Membri</h2>
              {members.length === 0 ? (
                <p className="text-muted text-sm text-center py-4">Nessun membro ancora</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-200 dark:border-surface-700">
                      <th className="table-header">#</th>
                      <th className="table-header">Nome</th>
                      <th className="table-header">Ruolo</th>
                      <th className="table-header">Stato</th>
                      <th className="table-header">Ingresso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m, i) => (
                      <tr key={m.id} className="table-row">
                        <td className="py-3 text-muted">{i + 1}</td>
                        <td className="py-3 font-medium text-heading">{m.full_name}</td>
                        <td className="py-3"><span className={positionBadge(m.position)}>{m.position}</span></td>
                        <td className="py-3"><span className="badge-green">{m.state}</span></td>
                        <td className="py-3 text-muted">
                          {new Date(m.joined_at).toLocaleDateString('it-IT')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Link to="/boxes" className="btn-secondary no-underline">← Tutti i box</Link>
            </div>

            {/* Il tuo stato nel box */}
            {(() => {
              const myMember = members.find(m => m.user_id === currentUser?.id);
              if (!myMember) return null;
              return (
                <div className="card">
                  <h2 className="text-lg font-semibold mb-4 text-heading">Il tuo percorso</h2>
                  <MovementTimeline
                    currentState={(myMember.position || 'NEW_DONOR').toUpperCase() as MovementState}
                  />
                </div>
              );
            })()}
          </>
        )}
      </div>
    </>
  );
}
